import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Props {
  children: ReactNode;
  next?: string;
}

/**
 * Guard: requires user to be a LINE OA friend.
 * - Not logged in → trigger LINE login
 * - Logged in but not OA friend → redirect to /skin-analysis/add-line
 * - Friend → render children
 * - Network/500 error → stay on page, show retry UI (fail-closed, don't auto-pass, don't auto-kick)
 */
export default function RequireFriend({ children, next = "/skin-analysis/select-mode" }: Props) {
  const navigate = useNavigate();
  const { member, login, isLoading } = useAuth();
  const [state, setState] = useState<"checking" | "ok" | "error">("checking");

  const runCheck = useCallback(async () => {
    if (!member?.lineAccessToken) return;
    setState("checking");
    try {
      const { data, error } = await supabase.functions.invoke("check-line-friend", {
        headers: { Authorization: `Bearer ${member.lineAccessToken}` },
        body: {},
      });
      if (error) {
        console.error("[RequireFriend] check failed:", error);
        toast.error("驗證好友狀態失敗，請稍後再試");
        setState("error");
        return;
      }
      if (data?.isFriend === true) {
        setState("ok");
      } else {
        navigate("/skin-analysis/add-line", { replace: true });
      }
    } catch (e) {
      console.error("[RequireFriend] exception:", e);
      toast.error("驗證好友狀態失敗，請稍後再試");
      setState("error");
    }
  }, [member, navigate]);

  useEffect(() => {
    if (isLoading) return;
    if (!member) {
      login(next);
      return;
    }
    if (!member.lineAccessToken) {
      login(next);
      return;
    }
    runCheck();
  }, [isLoading, member, login, next, runCheck]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-sm w-full text-center space-y-4">
          <h2 className="txt-h3 text-foreground">驗證好友狀態失敗</h2>
          <p className="txt-body-sm text-muted-foreground">
            目前無法確認您的 LINE 好友狀態，可能是網路或服務暫時不穩。請稍候再試一次。
          </p>
          <Button onClick={runCheck} className="rounded-full px-8">重試</Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
